/* eslint-disable */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchDataUri, APIError } from '../app/helper';
import { RootState } from '../app/store';

const sliceNameSpace = 'active_pdf_files' as const;

type TState = { status: 'idle' } | {
  status: 'success' | 'loading';
  data: IReportPDFs;
  currentJobId:string;
} | {
  status: 'error';
  error: string;
};

interface IReportPDFs {
  en: string; // because Blobs are not serializable
  de: string; // because Blobs are not serializable
}

const initialState: TState = { status: 'idle' };

const fetchPdf = (url: string, init?:RequestInit): Promise<string> => fetchDataUri(url, init).then((dataUri) => {
  if (!dataUri.startsWith('data:application/pdf;base64,')) throw new Error('not a pdf document', { cause: url });
  return dataUri;
});

export const get2Pdfs = createAsyncThunk<{ currentJobId:string, data:IReportPDFs }, void>(
  `${sliceNameSpace}/get_report_pdfs`,
  async (_, { getState, rejectWithValue, signal }) => {
    try {
      const rootState = getState() as RootState;
      if (rootState.jobs_list.status !== 'success') throw new Error('Reports are not fetched yet');

      const { activeReportId } = rootState.jobs_list;
      if (activeReportId === undefined) throw new Error('No active Report ID is set yet');

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { en_file_id, de_file_id } = rootState.jobs_list.reports[activeReportId];
      return {
        currentJobId: activeReportId,
        data: {
          en: await fetchPdf(`/tcml/fetch_pdf/${en_file_id}`, { signal }),
          de: await fetchPdf(`/tcml/fetch_pdf/${de_file_id}`, { signal }),
        },
      };
    } catch (error) {
      console.error({ error });
      if (error instanceof APIError) {
        const { details } = error;
        return rejectWithValue(details.message);
      }
      if (error instanceof Error) {
        const { cause, message } = error;
        console.error({ cause, message });
        // if ((cause as string).endsWith(en_file_id)) return rejectWithValue(`English file is ${message}`);
        // if ((cause as string).endsWith(de_file_id)) return rejectWithValue(`German file is ${message}`);
      }
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const rootState = getState() as RootState;
      if (rootState.jobs_list.status !== 'success') return false;

      const { activeReportId } = rootState.jobs_list;
      if (activeReportId === undefined) return false;

      const { status } = rootState[sliceNameSpace];
      // eslint-disable-next-line max-len
      if ((status === 'loading' || status === 'success') && activeReportId === rootState[sliceNameSpace].currentJobId) return false;

      return true;
    },
  },
);

const { actions, reducer } = createSlice({
  name: sliceNameSpace,
  initialState: initialState as TState,
  reducers: {
    clear: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(get2Pdfs.pending, (state: TState) => {
        state.status = 'loading';
      })
      .addCase(get2Pdfs.fulfilled, (_, { payload: { data, currentJobId } }) => ({
        status: 'success', data, currentJobId,
      }))
      .addCase(get2Pdfs.rejected, (_, { payload }) => ({
        status: 'error', error: payload as string || 'unknown error',
      }));
  },
});

export const selectPdfsSlice = (state: RootState):TState => state[sliceNameSpace];

export const { clear } = actions;

export default { reducer, namespace: sliceNameSpace };
